import React, { useState, useEffect, createContext, useContext, useCallback } from 'react';
import { apiFetch, readApiJson } from '../lib/api';

// ── Context pour partager l'org sélectionnée ────────────────────────────────

const OrgContext = createContext({
  organizations: [],
  selectedOrg: 'meteris',
  activeOrg: null,
  selectOrg: () => {},
});

const STORAGE_KEY = 'tk_selected_org';

// Normaliser en lowercase (= _id en DB) — les codes uppercase sont pour l'affichage seulement
function normalizeOrgId(val) {
  if (!val) return 'meteris';
  const v = String(val).toLowerCase();
  if (v === 'meteris') return 'meteris';
  if (v === 'mcf') return 'mcf';
  if (v === 'sci' || v === 'sci-les-tilleuls') return 'sci-les-tilleuls';
  return v;
}

export function OrgProvider({ children }) {
  const [organizations, setOrganizations] = useState([]);
  const [selectedOrg, setSelectedOrg] = useState(() => {
    try { return normalizeOrgId(localStorage.getItem(STORAGE_KEY)) || 'meteris'; }
    catch { return 'meteris'; }
  });

  useEffect(() => {
    apiFetch('/api/crm/organizations')
      .then(readApiJson)
      .then(res => {
        const orgs = res.organizations || [];
        setOrganizations(orgs);
        // Normalize legacy stored value + fallback if no longer accessible
        const normalized = normalizeOrgId(selectedOrg);
        if (orgs.length > 0 && !orgs.find(o => o.id === normalized)) {
          const fallback = orgs[0].id;
          setSelectedOrg(fallback);
          try { localStorage.setItem(STORAGE_KEY, fallback); } catch {}
        } else if (normalized !== selectedOrg) {
          setSelectedOrg(normalized);
          try { localStorage.setItem(STORAGE_KEY, normalized); } catch {}
        }
      })
      // Un échec ici vide le sélecteur pour toute la session : au minimum, le dire.
      .catch((e) => { console.warn('OrgSelector : liste des organisations indisponible', e); });
  }, []);

  const selectOrg = useCallback((orgId) => {
    setSelectedOrg(orgId);
    try { localStorage.setItem(STORAGE_KEY, orgId); } catch {}
  }, []);

  // Persister le défaut au montage si la clé est ABSENTE. apiFetch injecte
  // X-Active-Org depuis localStorage ; sans ça, un user au localStorage vide
  // voit le sélecteur sur le défaut (METERIS) mais aucun header n'est envoyé
  // → 400 « X-Active-Org header required » sur les endpoints qui l'exigent
  // (prévisionnel, banque). On teste `== null` (pas falsy) pour ne JAMAIS
  // écraser un choix explicite ni « Toutes » (id '' stocké volontairement).
  useEffect(() => {
    try {
      if (localStorage.getItem(STORAGE_KEY) == null && selectedOrg) {
        localStorage.setItem(STORAGE_KEY, selectedOrg);
      }
    } catch { /* localStorage indisponible : apiFetch gère l'absence */ }
  }, []);

  const activeOrg = organizations.find(o => o.id === selectedOrg) || null;

  return (
    <OrgContext.Provider value={{ organizations, selectedOrg, activeOrg, selectOrg }}>
      {children}
    </OrgContext.Provider>
  );
}

export function useOrg() {
  return useContext(OrgContext);
}

// ── Composant Toggle ────────────────────────────────────────────────────────

export default function OrgSelector({ value, onChange, showAll = false, compact = false, darkTheme = false }) {
  const { organizations, selectedOrg, selectOrg } = useOrg();

  // Fallback to context when used without props (uncontrolled mode)
  const currentValue = value !== undefined ? value : selectedOrg;
  const handleChange = onChange || selectOrg;

  if (organizations.length <= 1 && !showAll) return null;

  const items = showAll
    ? [{ id: '', name: 'Toutes', code: 'ALL' }, ...organizations]
    : organizations;

  const containerBg = darkTheme ? 'rgba(255,255,255,0.08)' : '#e8f0fe';
  const activeBg = darkTheme ? '#fff' : '#1a3a5c';
  const activeFg = darkTheme ? '#1a3a5c' : '#fff';
  const inactiveFg = darkTheme ? 'rgba(255,255,255,0.75)' : '#1a3a5c';

  return (
    <div style={{ display: 'flex', gap: 2, background: containerBg, borderRadius: 6, padding: 2 }}>
      {items.map(org => {
        const active = currentValue === org.id;
        return (
          <button
            key={org.id}
            onClick={() => handleChange(org.id)}
            style={{
              padding: compact ? '4px 10px' : '6px 14px',
              background: active ? activeBg : 'transparent',
              color: active ? activeFg : inactiveFg,
              border: 'none',
              borderRadius: 4,
              fontSize: compact ? 11 : 12,
              fontWeight: 700,
              cursor: 'pointer',
              fontFamily: "'IBM Plex Sans', sans-serif",
              transition: 'all 0.15s',
              whiteSpace: 'nowrap',
            }}
          >
            {org.code || org.name}
          </button>
        );
      })}
    </div>
  );
}
